import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { SiteHeader } from "@/components/site-header";
import { SiteFooter } from "@/components/site-footer";
import { cn } from "@/lib/utils";

export type LegalSection = {
  id: string;
  title: string;
  body: React.ReactNode;
};

interface Props {
  title: string;
  description: string;
  updated: string;
  sections: LegalSection[];
  numbered?: boolean;
}

export function LegalLayout({ title, description, updated, sections, numbered }: Props) {
  const { t } = useTranslation();
  const [active, setActive] = useState(sections[0]?.id);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
        if (visible) setActive(visible.target.id);
      },
      { rootMargin: "-30% 0px -60% 0px", threshold: 0 },
    );
    sections.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [sections]);

  return (
    <div className="flex min-h-screen flex-col bg-surface">
      <SiteHeader />

      <main className="mx-auto flex w-full max-w-6xl flex-[1_0_auto] flex-col px-4 py-12 sm:px-6 lg:py-16">
        <header className="border-b pb-8">
          <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">{t("legal.label")}</p>
          <h1 className="mt-2 font-display text-4xl font-bold tracking-tight sm:text-5xl">{title}</h1>
          <p className="mt-3 max-w-2xl text-muted-foreground">{description}</p>
          <p className="mt-3 text-xs text-muted-foreground">{t("legal.lastUpdated")} {updated}</p>
        </header>

        <div className="mt-10 grid shrink-0 content-start items-start gap-10 md:grid-cols-[220px_1fr]">
          {/* TOC */}
          <aside className="hidden md:sticky md:top-24 md:self-start md:block">
            <p className="mb-3 text-xs font-semibold uppercase tracking-widest text-muted-foreground">{t("legal.onThisPage")}</p>
            <nav className="space-y-1 border-s">
              {sections.map((s, i) => (
                <a
                  key={s.id}
                  href={`#${s.id}`}
                  className={cn(
                    "block border-s-2 -ml-px py-1.5 ps-4 text-sm transition-colors",
                    active === s.id
                      ? "border-foreground font-medium text-foreground"
                      : "border-transparent text-muted-foreground hover:text-foreground",
                  )}
                >
                  {numbered && <span className="me-2 tabular-nums">{i + 1}.</span>}
                  {s.title}
                </a>
              ))}
            </nav>
          </aside>

          {/* Content */}
          <article className="min-w-0 space-y-12 leading-relaxed">
            {sections.map((s, i) => (
              <section key={s.id} id={s.id} className="scroll-mt-24">
                <h2 className="font-display text-2xl font-semibold tracking-tight sm:text-3xl">
                  {numbered && <span className="me-3 text-muted-foreground tabular-nums">{i + 1}.</span>}
                  {s.title}
                </h2>
                <div className="mt-4 space-y-4 text-[15px] text-muted-foreground [&_strong]:text-foreground [&_li]:my-1 [&_ul]:ms-5 [&_ul]:list-disc">
                  {s.body}
                </div>
              </section>
            ))}
          </article>
        </div>
      </main>

      <div className="mt-auto shrink-0">
        <SiteFooter />
      </div>
    </div>
  );
}
